import React from 'react';
import { Select, Input, Checkbox } from 'antd';
import { useSites } from '../hooks/useSites';
import { useModalities } from '../hooks/useModalities';
import { hisStatusOptions } from '../utils/helper';
import {
  REPORT_STATUS_OPTIONS,
  DATE_RANGE_PRESETS,
  matchDatePreset,
  parseShortcut,
} from '../utils/shortcuts';

/**
 * Controlled filter-builder used by both the admin master-shortcut page and the
 * "Create your own shortcut" section of the profile dialog. Emits the compound
 * filter JSON object (not a string) via onChange.
 */
const ShortcutBuilder = ({ value, onChange }) => {
  const obj = parseShortcut(value) || {};
  const { centers } = useSites();
  const { modalityOptions } = useModalities();

  const update = (patch) => {
    const next = { ...obj, ...patch };
    // Drop empty keys to keep the stored JSON tidy.
    Object.keys(next).forEach((k) => {
      const v = next[k];
      if (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)) {
        delete next[k];
      }
    });
    onChange(next);
  };

  const currentPreset = matchDatePreset(obj.date_range);

  const onPresetChange = (presetValue) => {
    const preset = DATE_RANGE_PRESETS.find((p) => p.value === presetValue);
    if (!preset || preset.start === null) {
      update({ date_range: undefined });
    } else {
      update({ date_range: { start: preset.start, end: preset.end } });
    }
  };

  const fieldStyle = { width: '100%' };
  const rowStyle = { marginBottom: 12 };
  const labelStyle = {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: '#475569',
    marginBottom: 4,
  };

  return (
    <div>
      <div style={rowStyle}>
        <label style={labelStyle}>Modality</label>
        <Select
          mode="multiple"
          className="single-line-tags"
          allowClear
          placeholder="Any modality"
          style={fieldStyle}
          value={obj.modality || []}
          options={modalityOptions}
          onChange={(val) => update({ modality: val })}
        />
      </div>

      <div style={rowStyle}>
        <label style={labelStyle}>Report Status</label>
        <Select
          mode="multiple"
          className="single-line-tags"
          allowClear
          placeholder="Any status"
          style={fieldStyle}
          value={obj.pacs_status || []}
          options={REPORT_STATUS_OPTIONS}
          onChange={(val) => update({ pacs_status: val })}
        />
      </div>

      <div style={rowStyle}>
        <label style={labelStyle}>HIS Status</label>
        <Select
          mode="multiple"
          className="single-line-tags"
          allowClear
          placeholder="Any HIS status"
          style={fieldStyle}
          value={obj.po_his_status || []}
          options={hisStatusOptions}
          onChange={(val) => update({ po_his_status: val })}
        />
      </div>

      <div style={rowStyle}>
        <label style={labelStyle}>Site</label>
        <Select
          mode="multiple"
          className="single-line-tags"
          allowClear
          placeholder="Any site"
          style={fieldStyle}
          value={obj.center || []}
          options={centers}
          onChange={(val) => update({ center: val })}
        />
      </div>

      <div style={rowStyle}>
        <label style={labelStyle}>Date Range</label>
        <Select
          style={fieldStyle}
          value={currentPreset ? currentPreset.value : 'ANY'}
          options={DATE_RANGE_PRESETS.map((p) => ({ label: p.label, value: p.value }))}
          onChange={onPresetChange}
        />
      </div>

      <div style={rowStyle}>
        <label style={labelStyle}>Referring Doctor</label>
        <Input
          allowClear
          placeholder="Any"
          value={obj.po_ref_doc || ''}
          onChange={(e) => update({ po_ref_doc: e.target.value })}
        />
      </div>

      <div style={rowStyle}>
        <label style={labelStyle}>Body Part / Study Desc</label>
        <Input
          allowClear
          placeholder="Any"
          value={obj.po_body_part || ''}
          onChange={(e) => update({ po_body_part: e.target.value })}
        />
      </div>

      {/* Boolean flags — stored as true when checked, omitted when unchecked. */}
      <div style={rowStyle}>
        <label style={labelStyle}>Flags</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px' }}>
          <Checkbox
            checked={!!obj.show_emergency}
            onChange={(e) => update({ show_emergency: e.target.checked ? true : undefined })}
          >
            Emergency
          </Checkbox>
          <Checkbox
            checked={!!obj.show_rereported}
            onChange={(e) => update({ show_rereported: e.target.checked ? true : undefined })}
          >
            Re-reported
          </Checkbox>
          <Checkbox
            checked={!!obj.show_mlc}
            onChange={(e) => update({ show_mlc: e.target.checked ? true : undefined })}
          >
            MLC
          </Checkbox>
        </div>
      </div>
    </div>
  );
};

export default ShortcutBuilder;
